<template>
  <div>
    <SelectorPanel
      type="search"
      :text="labels.search"
      :disabled="disabled"
      :force-close="forceClose"
      :url-path="urlPath"
      @click="handlePanelOpen"
    >
      <v-container
        class="pa-0 hyp-search-container"
        :class="{
          'hyp-search-container-full hyp-search-library-container': showLibrary,
        }"
        :style="containerStyle"
        fluid
      >
        <div class="hyp-search-filter-container">
          <SelectorPanelHeader
            type="search"
            :title="labels.search"
            class="pb-2"
            @click-close="handleClickClose"
          >
          </SelectorPanelHeader>

          <div>
            <div
              v-if="!isSearchLayoutChangesUnlocked"
              class="new-search-btn-container py-5"
            >
              <v-btn
                small
                outlined
                block
                color="primary"
                @click="handleClickNewSearch"
                >New Search</v-btn
              >
            </div>

            <SearchContentFilters
              :selected-search-params="selectedSearchParams"
              :date-range-start-days-back="dateRangeStartDaysBack"
              :date-range-max-date-back="dateRangeMaxDateBack"
              :filter-counts="filterCounts"
              :reset-timestamp="resetFilters"
              :current-content-type="contentType"
              :riskCategories="riskCategories"
              @change-keywords="handleChangeKeywords"
              @change-keywords-builder="handleChangeKeywordsBuilder"
              @change-date-range="handleChangeDateRange"
              @change-bounds="handleChangeBounds"
              @change-locations="handleChangeLocations"
              @change-places="handleChangePlaces"
              @change-events="handleChangeEvents"
              @change-events-v2="handleChangeEventsV2"
              @change-hotspots-filter="handleChangeHotspotsFilter"
              @change-sources="handleChangeSources"
              @change-sentiment="handleChangeSentiment"
              @change-emotions="handleChangeEmotions"
              @change-has-filters-set="handleChangeHasFiltersSet"
              @reset="handleClickReset"
              @change-should-pull-data="handleChangeShouldPullData"
              @change-tab="handleChangeEventType"
              @change-risk-categories="handleChangeRiskCategories"
              @change-severity="handleChangeSeverity"
              @change-geolocation-precisions="handleChangeGeolocationPrecisions"
              @change-risk-category-tags="handleChangeRiskCategoryTags"
              @click-clear="handleClickNewSearch"
              @show-library="handleClickLoadLibrary"
            ></SearchContentFilters>
          </div>
        </div>

        <div class="hyp-search-content-container" v-if="!showLibrary">
          <SearchContent
            :refresh="refresh"
            :keywords="keywords"
            :keywords-builder="keywordsBuilder"
            :date-range-start-days-back="dateRangeStartDaysBack"
            :date-range="dateRange"
            :bounds="bounds"
            :bounds-type="boundsType"
            :aoi-id="aoiId"
            :places="places"
            :radius="radius"
            :poisDistUnits="poisDistUnits"
            :sources="sources"
            :events="events"
            :events-v2="eventsV2"
            :hotspotsOnly="hotspotsOnly"
            :is-minimized="isMinimized"
            :should-pull-data="shouldPullData"
            :risk-categories="riskCategories"
            :severity="severity"
            :has-deep-link="hasDeepLink"
            :risk-category-tags="riskCategoryTags"
            :geolocation-precisions="geolocationPrecisions"
            :sentiment="sentiment"
            :emotions="emotions"
            @filter-counts-update="handleFilterCountsUpdate"
            @click-minimize="handleClickMinimize"
            @click-close="handleClickClose"
            @change-deep-link="changeDeepLink"
          ></SearchContent>
        </div>
        <div class="hyp-search-content-container" v-if="showLibrary">
          <SearchLibrary
            @close-library="handleClickCloseLibrary"
            @click-close="handleClickClose"
            @view-search-results="viewSearchResults"
          ></SearchLibrary>
        </div>
      </v-container>
    </SelectorPanel>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashDebounce from "lodash/debounce";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import {
  EVENT_ACTIONS,
  SEARCH_CATEGORIES,
  CONTENT_TYPES,
} from "@/common/constants/analytics";

import { LABELS } from "@/common/constants/labels";
import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_SAVED_SEARCH,
  MODULE_PREFIX_USER,
} from "@/store";
import { hasEssentialsAccess } from "@/services/user/user.service";
import {
  GET_SAVED_SEARCH_DETAILS,
  GET_SAVED_SEARCH_BUTTON_ACTION,
} from "@/store/actions.type";
import { SET_RESET_MAP_ICONS } from "@/store/mutations.type";
import { SUPPORT_EMAIL, ROUTE_URLS } from "@/common/constants/misc";
import { getTodayStartEndDate, nowUtc } from "@/common/date.utilities";
import { LeftSidePanelWidthNumber } from "@/common/constants/layout";

export default {
  name: "SearchSelector",
  components: {
    SelectorPanel: () => import("@/components/common/SelectorPanel"),
    SelectorPanelHeader: () =>
      import("@/components/common/SelectorPanelHeader"),
    SearchContentFilters: () =>
      import("@/components/search/SearchContentFilters"),
    SearchContent: () => import("@/components/search/SearchContent"),
    SearchLibrary: () => import("@/components/search/library/SearchLibrary"),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      keywords: null,
      keywordsBuilder: null,
      dateRange: null,
      dateRangeStartDaysBack: 2,
      dateRangeMaxDateBack: null,
      boundsType: "geography",
      bounds: null,
      aoiId: null,
      places: null,
      radius: null,
      poisDistUnits: "mi",
      events: null,
      eventsV2: { eventType: "all", mapType: "marker" },
      hotspotsOnly: hasEssentialsAccess(),
      sources: null,
      isOpen: false,
      filterCounts: null,
      forceClose: null,
      isMinimized: false,
      refresh: new Date().toISOString(),
      hasFiltersSet: false,
      shouldPullData: false,
      resetFilters: new Date().toISOString(),
      labels: LABELS,
      riskCategories: null,
      contentType: null, //Value is always one of CONTENT_TYPES
      searchid: null,
      notificationExists: false,
      severity: null,
      sentiment: null,
      emotions: null,
      geolocationPrecisions: null,
      hasDeepLink: Boolean(lodashGet(this.$route, "params.searchid")),
      riskCategoryTags: null,
      urlPath: "/search",
      libraryUrlPath: ROUTE_URLS.searchLibrary,
      selectorBtnIconText: `Search is now the place to discover all our AI-detected and human
        verified events. We will soon be adding additional content sets to
        our integrated Search panel to enhance your experience. To find out
        more, please <a href="mailto:${SUPPORT_EMAIL}">contact us</a>.`,
      showLibrary: false,
      selectedSearchParams: null,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      resetMapIcons: MODULE_PREFIX_MAP + "resetMapIcons",
      savedSearchDetails: MODULE_PREFIX_SAVED_SEARCH + "savedSearchDetails",
      renaming: MODULE_PREFIX_SAVED_SEARCH + "renaming",
    }),
    isSearchLayoutChangesUnlocked() {
      return this.featureUnlocked("search-layout-changes");
    },
    isSearchLibraryUnlocked() {
      return this.featureUnlocked("search-library") && !hasEssentialsAccess();
    },
    fullScreen() {
      return this.isSearchLibraryUnlocked && this.showLibrary;
    },
    containerStyle() {
      let width = "723px";
      if (this.isMinimized) {
        width = "395px";
      }
      if (this.fullScreen) {
        const w = window.innerWidth - LeftSidePanelWidthNumber;
        width = `${w}px`;
      }
      return {
        width: width,
      };
    },
    prefDistanceUnits() {
      const unit = this.preferences?.poisDistUnits;
      return unit ? (unit === "miles" ? "mi" : "km") : "mi";
    },
  },
  mounted() {
    this.searchid = this.$route.params?.searchid;
    if (this.searchid === "library" && this.isSearchLibraryUnlocked) {
      this.handleClickLoadLibrary();
    }
  },
  methods: {
    changeDeepLink(val) {
      this.hasDeepLink = val;
    },
    handlePanelOpen(open) {
      if (this.showLibrary) {
        this.showLibrary = false;
        window.history.replaceState({}, "Seerist", this.urlPath);
      }
      lodashDebounce(() => {
        this.isOpen = open;
      }, 1000)();
    },
    handleClickClose() {
      if (this.showLibrary) {
        this.showLibrary = false;
        window.history.replaceState({}, "Seerist", this.urlPath);
      }
      this.forceClose = new Date().toISOString();
    },
    handleClickMinimize() {
      this.isMinimized = !this.isMinimized;
    },
    handleChangeKeywords(keywords) {
      this.keywords = keywords;
      this.handleChangeShouldPullData(true);
    },
    handleChangeKeywordsBuilder(keywordsBuilder) {
      this.keywordsBuilder = keywordsBuilder === "" ? null : keywordsBuilder;
      this.handleChangeShouldPullData(true);
    },
    handleChangeDateRange(dates) {
      this.dateRange = dates;
      this.handleChangeShouldPullData(true);
    },
    handleChangeBounds(obj, dontPullData) {
      this.boundsType = obj.boundsType;
      this.bounds = obj.bounds;
      this.handleChangeShouldPullData(!dontPullData);
    },
    handleChangeLocations(params) {
      this.boundsType = params.boundsType;
      this.bounds = params.boundsType === "mapextent" ? params.bounds : null;
      this.poisDistUnits = params.poisDistUnits || this.prefDistanceUnits;
      this.places = params.places;
      this.radius = params.radius;
      this.aoiId = params.aoiId;
      this.handleChangeShouldPullData(true);
    },
    handleChangePlaces(params) {
      this.poisDistUnits = params.poisDistUnits;
      this.places = params.places;
      this.radius = params.radius;
      this.handleChangeShouldPullData(true);
    },
    handleClickReset() {
      this.selectedSearchParams = null;
      this.resetFilters = new Date().toISOString();
    },
    async handleClickNewSearch(isMapIconsCleared) {
      if (isMapIconsCleared && isMapIconsCleared !== true) {
        await this.$store.commit(
          MODULE_PREFIX_MAP + SET_RESET_MAP_ICONS,
          new Date()
        );
      }
      // reset dates and clear old search details from email deep links to /savedsearch/:id
      if (this.hasDeepLink) {
        const blankSearch = {
          params: {
            range: [-3, "-0"], // not sure what this is for
            start: getTodayStartEndDate(dayjs(nowUtc()).add(-2, "days")).start,
            end: getTodayStartEndDate(dayjs(nowUtc())).end,
          },
        };

        this.$store.dispatch(
          MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS,
          blankSearch
        );

        delete this.$route.query.start;
        delete this.$route.query.end;
        this.hasDeepLink = false;
        this.$router.push("/home");
      }

      this.selectedSearchParams = null;
      this.resetFilters = new Date().toISOString();
      lodashDebounce(() => {
        this.handleChangeShouldPullData(false);
      }, 1000)();
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search,
        EVENT_ACTIONS.clicked,
        "New Search"
      );
    },
    handleChangeEvents(events, eventType, mapType) {
      this.dateRangeMaxDateBack = null;
      this.events = events;
      this.handleChangeShouldPullData(true);
      this.eventsV2 = {
        eventType: eventType || "all",
        events: events,
        mapType: mapType || "marker",
      };
      this.dateRangeMaxDateBack = null;
      if (eventType === "verified") {
        this.dateRangeMaxDateBack = "2008-01-08T00:00:00.000Z";
      }
    },
    handleChangeEventsV2(events) {
      this.eventsV2 = events;
      this.dateRangeMaxDateBack = null;
      if (lodashGet(events, "eventType") === "verified") {
        this.dateRangeMaxDateBack = "2008-01-08T00:00:00.000Z";
        //Make sure a default verified type is set since we don't show both at the same time
        if (!events?.verifiedType) {
          this.eventsV2.verifiedType = "security";
        }
      }
      const selectedTopics = events.events ? events.events.split(",") : null;
      if (selectedTopics && selectedTopics.length > 0) {
        this.contentType = CONTENT_TYPES.events;
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeHotspotsFilter(params) {
      this.hotspotsOnly = params.hotspotsOnly;
      this.handleChangeShouldPullData(true);
    },
    handleChangeSources(sources) {
      this.sources = sources;
      this.contentType = sources.analysis
        ? CONTENT_TYPES.analysis
        : CONTENT_TYPES.events;
      this.handleChangeShouldPullData(true);
    },
    handleFilterCountsUpdate(filterCounts) {
      this.$set(this, "filterCounts", filterCounts);
    },
    handleChangeHasFiltersSet(val) {
      this.hasFiltersSet = val;
    },
    handleChangeShouldPullData(val) {
      this.shouldPullData = val;
    },
    handleChangeEventType(params) {
      this.eventsV2.eventType = params.eventType;
      if (params && params.length > 0) {
        this.contentType = CONTENT_TYPES.events;
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeRiskCategories(params) {
      this.riskCategories = params;
      if (params && params.length > 0) {
        this.contentType = CONTENT_TYPES.analysis;
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeSeverity(params) {
      if (
        lodashHas(params, "severityMin") ||
        lodashHas(params, "severityMax")
      ) {
        this.severity = [params.severityMin || 0, params.severityMax || 10];
        this.handleChangeShouldPullData(true);
      } else {
        this.severity = null;
      }
    },
    handleChangeSentiment(params) {
      this.sentiment = params;
      if (params && Array.isArray(params) && params.length) {
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeEmotions(params) {
      this.emotions = params;
      if (params && Array.isArray(params) && params.length) {
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeGeolocationPrecisions(params) {
      this.geolocationPrecisions = params;
      if (params && params.length > 0) {
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeRiskCategoryTags(params) {
      this.riskCategoryTags = params;
      if (params && params.length > 0) {
        this.contentType = CONTENT_TYPES.analysis;
        this.handleChangeShouldPullData(true);
      }
    },
    handleClickSaveAsSearch() {
      this.$store.dispatch(
        MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_BUTTON_ACTION,
        "saveas"
      );
    },
    handleClickSaveSearch() {
      this.$store.dispatch(
        MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_BUTTON_ACTION,
        "save"
      );
    },
    handleClickLoadLibrary() {
      this.showLibrary = true;
      window.history.replaceState({}, "Seerist", this.libraryUrlPath);
    },
    handleClickCloseLibrary() {
      this.showLibrary = false;
      if (this.$route?.params?.searchid) {
        delete this.$route?.params?.searchid;
      }
      window.history.replaceState({}, "Seerist", this.urlPath);
      lodashDebounce(() => {
        this.refresh = new Date().toISOString();
      }, 1000)();
    },
    viewSearchResults(search) {
      // View search results by search params
      this.selectedSearchParams = search.params
        ? JSON.parse(search.params)
        : null;
      this.handleClickCloseLibrary();
    },
  },
  watch: {
    isOpen() {
      if (this.isOpen) {
        this.refresh = new Date().toISOString();
      }
    },
    "preferences.poisDistUnits"() {
      this.handleClickNewSearch();
    },
    resetMapIcons: {
      handler(newValue, oldValue) {
        if (oldValue !== newValue) {
          this.handleClickNewSearch(true);
        }
      },
    },
    contentType: {
      handler(newVal) {
        if (newVal === CONTENT_TYPES.events) {
          this.riskCategories = null;
        } else {
          this.eventsV2.events = null;
        }
      },
    },
    savedSearchDetails: {
      handler() {
        this.notificationExists =
          this.savedSearchDetails &&
          this.savedSearchDetails.userId === this.savedSearchDetails.ownerId &&
          !!this.savedSearchDetails.frequency;
      },
    },
  },
  beforeDestroy() {
    this.isOpen = false;
  },
};
</script>

<style scoped>
.hyp-search-container {
  padding: 0 !important;
  display: grid;
  grid-template-columns: 345px 1fr;
  float: left;
}
.hyp-search-container-full.hyp-search-container {
  display: block;
  padding: 0 12px !important;
}
.hyp-search-library-container .hyp-search-filter-container {
  position: absolute;
  left: -10000px;
}
.hyp-search-filter-container {
  grid-column: 1;
  box-sizing: border-box;
  height: 100vh;
  display: grid;
  grid-template-rows: 40px 1fr 64px;
  grid-row-gap: 20px;
}
.hyp-search-content-container {
  grid-column: 2;
  box-sizing: border-box;
  height: 100vh;
}
.bottom-controls-container {
  background-color: transparent;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  left: 0;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  width: 321px;
  height: 56px;
  z-index: 2;
}
.bottom-controls-inner {
  width: 320px;
}
.new-search-btn-container {
  text-align: center;
  width: 320px;
  margin-left: 12px;
}
</style>
